import { watch } from "vue"
import router from "../router"
import { apiClient } from "@/services/api"
import { hasUnsavedChanges } from "@/services/unsavedChanges"
import { openReportPanels } from "@/composables/useReportPanel"

// What a bug report needs besides the words: what the person did in the minutes before, what was open,
// and which messages were on screen. The replay only buffers the last 60 seconds and the URL names the
// page, not the report open on it, so this goes onto the feedback event itself:
//
// - `contexts.activity`: the last requests and page, tab and report panel changes, one line each.
// - `contexts.app_state`: the route, and each open report panel with its report; tags `report_type` and
//   `report_id` for the one in front.
// - `contexts.screen_messages`: validation messages, alerts and snackbars on screen at sending.
//
// Contexts and tags rather than breadcrumbs: the SDK does apply breadcrumbs to a feedback event, but
// Sentry shows none on it (LEWARA-FRONTEND-6N). Everything here follows the replay's privacy line (see
// the unmask list in plugins/sentry.js): ids, counts, route names and our own wording. No typed value,
// no search text, no query string.
//
// Size and shape are set by what reaches the event whole. The SDK normalises `contexts` to depth 3,
// which turns an object inside an array into "[Object]", so a panel is a key of `app_state`, not an
// entry in a list. Relay then drops what goes over 8192 bytes per context, which the byte budgets
// below stay under.

/** Kept per kind, so a page load's burst of requests does not push out how the person got there. */
const REQUESTS_KEPT = 30
const STEPS_KEPT = 15
/** The trail's lines, in UTF-8 bytes: well under Relay's 8192 per context. */
const TRAIL_BYTES = 7000
const MESSAGES_KEPT = 12
const MESSAGE_LENGTH = 200
/** Real paths stay far below; capped so a line keeps its outcome whatever the path. */
const PATH_LENGTH = 120

/**
 * The notification poll, every 5 minutes whatever the person does. Other polls (the attendance on the
 * time-tracking review, a running data export) fold into one line, see `foldRepeat`. `/version.json`
 * is fetched outside `apiClient` and never reaches the trail.
 */
const isPoll = (method, path) => method === "GET" && path === "/api/notifications"

/**
 * The selectors the replay unmasks, most telling first: when there are more than {@link MESSAGES_KEPT},
 * a field's hint is what gets left out, not the snackbar.
 */
const MESSAGE_KINDS = [
  ["snackbar", ".v-snackbar__content"],
  ["alert", ".v-alert__content"],
  ["field error", ".v-input--error .v-messages__message"],
  ["field", ".v-messages__message"],
]

const requests = []
const steps = []
/**
 * Order of recording. The trail is sorted by it, not by time: a page change and the requests of the page
 * it opens often share a millisecond, and a stable sort by time would put the requests first.
 */
let recorded = 0
/** Where a request's entry rides along on its axios config, to the response or error that answers it. */
const TRAIL = Symbol("feedback trail")

const keep = (list, entry, max) => {
  list.push(entry)
  if (list.length > max) list.shift()
}

const cap = (text, max) => (text.length > max ? `${text.slice(0, max - 1)}…` : text)

const pad = n => String(n).padStart(2, "0")
/** Local time, as on the clock in the person's screenshot. */
const clock = at => {
  const date = new Date(at)
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}
const duration = ms => (ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`)

/**
 * The request's path with its ids, without query string. Every backend path variable is an id, an
 * enum or a date, so the path names nothing of the customer's. An absolute URL is someone else's
 * server: its host only.
 */
function pathOf(config) {
  const url = String(config.url ?? "").split(/[?#]/)[0]
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(url)) return `${new URL(url).host}/…`
  const base = String(config.baseURL ?? "").replace(/\/$/, "")
  return cap(url.startsWith("/") ? base + url : `${base}/${url}`, PATH_LENGTH)
}

/** A status, with the violation code a refusal names; or why there is no status. */
function outcomeOf(error) {
  const response = error?.response
  if (response) {
    const violation = response.data?.violationCode
    return /^[A-Z][A-Z\d_]{0,63}$/.test(violation ?? "") ? `${response.status} ${violation}` : String(response.status)
  }
  if (error?.code === "ERR_CANCELED") return "cancelled"
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") return "timed out"
  return "no answer"
}

/**
 * A GET answered like the one right before it, with no page or panel change since, joins that one's
 * line: a page polling every few seconds would otherwise fill the trail and push out what the person
 * did. Writes are never folded; each save keeps its own line and time.
 */
function foldRepeat(entry) {
  const index = requests.indexOf(entry)
  const previous = requests[index - 1]
  if (entry.method !== "GET" || !previous) return
  if (previous.method !== entry.method || previous.path !== entry.path || previous.outcome !== entry.outcome) return
  if ((steps.at(-1)?.order ?? -1) > (previous.lastOrder ?? previous.order)) return
  previous.repeats = (previous.repeats ?? 1) + 1
  previous.lastAt = entry.at
  previous.lastOrder = entry.order
  requests.splice(index, 1)
}

function settle(config, outcome) {
  const entry = config?.[TRAIL]
  if (!entry || entry.outcome) return
  entry.outcome = outcome
  entry.ms = Date.now() - entry.at
  foldRepeat(entry)
}

function requestLine(entry, now) {
  const request = `${clock(entry.at)} ${entry.method} ${entry.path}`
  if (!entry.outcome) return `${request} → still waiting after ${duration(now - entry.at)}`
  const repeats = entry.repeats ? `, ${entry.repeats}× until ${clock(entry.lastAt)}` : ""
  return `${request} → ${entry.outcome} in ${duration(entry.ms)}${repeats}`
}

const step = text => {
  // A query-only change (a filter, a search) reads the same as the page before it here.
  if (steps.at(-1)?.text === text) return
  keep(steps, { at: Date.now(), order: recorded++, text }, STEPS_KEPT)
}

/** The route's name, or for the catch-all, which has none, its pattern. Never the path typed. */
const routeName = route => String(route.name ?? route.matched?.at(-1)?.path ?? "unnamed")

/** Only params that hold an id: `/reset_password/:identityId/:token` has one that must stay out. */
function idParams(route) {
  const params = {}
  for (const [key, value] of Object.entries(route.params ?? {})) {
    const text = Array.isArray(value) ? value.join("/") : String(value)
    if (/Id$/.test(key) && /^[\w-]{1,64}$/.test(text)) params[key] = text
  }
  return params
}

/** The tab a page keeps in its hash (`useTabHash`). Anything else there is not ours to send. */
const hashTab = route => {
  const tab = route.hash?.slice(1) ?? ""
  return /^[\w-]{1,40}$/.test(tab) ? tab : ""
}

function pageLine(route) {
  const params = Object.entries(idParams(route)).map(([key, value]) => `${key}=${value}`)
  const tab = hashTab(route)
  return ["page", routeName(route), ...params, tab && `#${tab}`].filter(Boolean).join(" ")
}

const panelSummary = state =>
  [state.id || "new", state.revision && "revision", state.tab, state.minimized && "minimised"].filter(Boolean).join(" ")

/**
 * Starts the trail: every API request, page and tab change and report panel change from now on.
 * `openFeedbackWidget` marks when the form was opened, so the time spent writing is not mistaken for
 * time spent in the app.
 */
export function trackActivity(client) {
  apiClient.interceptors.request.use(config => {
    try {
      const method = String(config.method ?? "get").toUpperCase()
      const path = pathOf(config)
      if (!isPoll(method, path)) {
        const entry = { at: Date.now(), order: recorded++, method, path }
        keep(requests, entry, REQUESTS_KEPT)
        config[TRAIL] = entry
      }
    } catch {
      // The trail is a diagnosis; the request goes out whatever happens to it.
    }
    return config
  })
  apiClient.interceptors.response.use(
    response => {
      settle(response.config, String(response.status))
      return response
    },
    error => {
      settle(error?.config, outcomeOf(error))
      return Promise.reject(error)
    }
  )

  router.afterEach((to, _from, failure) => {
    if (!failure) step(pageLine(to))
  })

  watch(
    () => Object.fromEntries(openReportPanels().map(state => [state.panel, panelSummary(state)])),
    (now, before) => {
      for (const panel of new Set([...Object.keys(before), ...Object.keys(now)])) {
        if (now[panel] !== before[panel]) step(`panel ${panel} ${now[panel] ?? "closed"}`)
      }
    }
  )

  client.on("openFeedbackWidget", () => step("feedback form opened"))
}

/** Oldest first, cut from the old end until it fits the byte budget. */
function trail(now) {
  const lines = [
    ...requests.map(entry => ({ order: entry.order, line: requestLine(entry, now) })),
    ...steps.map(entry => ({ order: entry.order, line: `${clock(entry.at)} ${entry.text}` })),
  ]
    .sort((a, b) => a.order - b.order)
    .map(({ line }) => line)
  const encoder = new TextEncoder()
  while (lines.length && encoder.encode(JSON.stringify(lines)).length > TRAIL_BYTES) lines.shift()
  return lines
}

function appState() {
  const route = router.currentRoute.value
  const state = {
    route: routeName(route),
    params: idParams(route),
    tab: hashTab(route),
    unsaved_changes: hasUnsavedChanges.value,
  }
  for (const { panel, ...report } of openReportPanels()) state[panel] = report
  return state
}

/** The panel a report is about: the one in front, else one left minimised. */
function reportTags() {
  const panels = openReportPanels()
  const panel = panels.find(state => !state.minimized) ?? panels[0]
  return panel ? { report_type: panel.panel, report_id: panel.id || "new" } : {}
}

/**
 * Rendered and not hidden: an inactive tab's window, a minimised panel's body and a closed drawer
 * (Vuetify makes it `inert`) all hold messages nobody can see. `sentry-mask` re-masks in the replay,
 * so it keeps a message out of here too.
 */
function isShown(element) {
  if (element.closest("[inert], .sentry-mask")) return false
  if (typeof element.checkVisibility !== "function") return element.getClientRects().length > 0
  return element.checkVisibility({
    checkOpacity: true,
    checkVisibilityCSS: true,
    opacityProperty: true,
    visibilityProperty: true,
  })
}

/** `kind: text`, each once. A field's error is matched by both field selectors and counts as the error. */
function screenMessages() {
  const taken = new Set()
  const messages = new Set()
  for (const [kind, selector] of MESSAGE_KINDS) {
    for (const element of document.querySelectorAll(selector)) {
      if (messages.size >= MESSAGES_KEPT) return [...messages]
      if (taken.has(element) || !isShown(element)) continue
      taken.add(element)
      const text = element.textContent.replace(/\s+/g, " ").trim()
      if (text) messages.add(`${kind}: ${cap(text, MESSAGE_LENGTH)}`)
    }
  }
  return [...messages]
}

/**
 * `client.on("beforeSendFeedback")`: fires synchronously while the form is still open, so the page
 * under it is as the person left it. Set on the event, not the scope, for the reason in
 * sentryFeedbackPriority.js. The SDK does not catch a throwing hook, and a throw here would lose the
 * feedback, so each part fails on its own.
 */
export function addFeedbackContext(event) {
  const contexts = {}
  const now = Date.now()
  try {
    contexts.activity = { sent_at: clock(now), trail: trail(now) }
  } catch {
    // Sent without it.
  }
  try {
    contexts.app_state = appState()
    event.tags = { ...event.tags, ...reportTags() }
  } catch {
    // Sent without it.
  }
  try {
    const messages = screenMessages()
    contexts.screen_messages = { count: messages.length, messages }
  } catch {
    // Sent without it.
  }
  event.contexts = { ...event.contexts, ...contexts }
}
